import { motion } from "motion/react";
import {
  CongressImageLightboxHost,
  MORPH_EASE,
  MORPH_SECONDS,
} from "./CongressImageLightbox";
import {
  cardImageSrc,
  cropStyle,
  lightboxSrc,
  type DocumentCrop,
  type DocumentImageSizes,
} from "../../domains/congress/documentImage";
import { useDocumentLightbox } from "../../domains/congress/useDocumentLightbox";

/**
 * An IMAGE congress document in the Resources feed: full width, a FIXED 16:9
 * frame, the CX's reader-facing label above the title.
 *
 * Why 16:9 and not something taller: the lightbox shows the full image, so this
 * card does not need to be legible — it needs to be recognisable and inviting.
 * Legibility is the lightbox's job. 16:9 is compact in a stacked feed, reads
 * instantly as "an image", and crops less off a typically very wide floor plan.
 * Fixing the ratio also keeps feed height predictable whatever gets uploaded.
 *
 * The crop is the CX's intentional framing, stored as fractions and applied here
 * ONLY. Tapping opens the full, uncropped image — so a good card frame never
 * costs the reader half the map.
 *
 * No thumbnail, no detail line, no detail icon and no tap hint: the image IS the
 * visual and its size is the affordance. Those fields are not merely unused —
 * the DB CHECK makes them impossible on this arm.
 */
export function CongressImageCard({
  imageUrl,
  imageSizes,
  crop,
  label,
  title,
  layoutId,
  onOpen,
}: {
  imageUrl: string | null;
  imageSizes: DocumentImageSizes;
  crop: DocumentCrop;
  /** The CX-written reader-facing label, e.g. "PLAN DU CONGRÈS". */
  label: string;
  title: string;
  /** Shared with the lightbox so motion morphs between the two. */
  layoutId: string;
  /** Analytics only — the card owns opening its own lightbox. */
  onOpen: () => void;
}): React.JSX.Element {
  // No local state or refs: this is a business component, and the transition's
  // whole lifecycle lives in one hook (see useDocumentLightbox). The card renders
  // and calls callbacks.
  const {
    imageRef,
    open,
    aspectRatio,
    exitInPlace,
    openLightbox,
    closeLightbox,
  } = useDocumentLightbox(imageSizes);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          onOpen();
          openLightbox();
        }}
        className="flex w-full cursor-pointer flex-col gap-2 text-left font-dm-sans"
      >
        {/* Fixed 16:9 box; the crop is applied by scaling and offsetting the image
          inside it, which is why the box clips. */}
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-ds-slate-100">
          {imageUrl === null ? (
            // Degraded, not broken: the Image row can be deleted from under the
            // document (the FK is ON DELETE SET NULL, deliberately — see the
            // migration's note on the division of labour with the CHECK).
            <div className="size-full bg-ds-slate-100" />
          ) : (
            <motion.img
              ref={imageRef}
              layoutId={layoutId}
              src={cardImageSrc(imageUrl, imageSizes)}
              alt={title}
              style={cropStyle(crop)}
              // THIS image is what travels on the way BACK — motion promotes the
              // surviving stack member on close — so it must animate on the same
              // timing as the opening morph. Without it, motion used its DEFAULT
              // layout spring here, so the close was a spring answering a tween,
              // and because the promoted member owns `safeToRemove`, the spring's
              // settling time gated the whole overlay's unmount: 462ms, versus the
              // 340ms its own exits needed. That is what kept an invisible
              // full-screen layer over the feed.
              transition={{
                layout: { duration: MORPH_SECONDS, ease: MORPH_EASE },
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-0.5">
          <p className="m-0 text-[11px]/4 font-semibold tracking-wide text-ds-mint-800 uppercase">
            {label}
          </p>
          <p className="m-0 text-sm leading-[1.2] font-semibold tracking-[-0.13px] text-ds-black">
            {title}
          </p>
        </div>
      </button>
      <CongressImageLightboxHost
        open={open}
        // The overlay OPENS on the exact rendition the card is showing, so the
        // morph never waits on the network, then upgrades to the original itself.
        cardSrc={imageUrl === null ? null : cardImageSrc(imageUrl, imageSizes)}
        originalSrc={
          imageUrl === null ? null : lightboxSrc(imageUrl, imageSizes)
        }
        aspectRatio={aspectRatio}
        alt={title}
        layoutId={layoutId}
        exitInPlace={exitInPlace}
        onClose={closeLightbox}
      />
    </>
  );
}
